import { useMemo } from 'react';
import { LogSet, type LogSet as LogSetT } from '@heraion/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import { useAsyncResource } from './useAsyncResource';
import { asyncStorageResourceCache } from './state/asyncListCache';

// LG-49 — the answer for an exercise the rolling window cannot see.
//
// ⭐ THE BUG THIS EXISTS FOR. `useEntryHistory` fetches ONE window —
// `/api/sessions?include=sets&limit=50` — and every entry's history is read
// out of it. Fifty sessions sounds generous; at Antenore's cadence (median
// one day) it is about three months. A lift resumed after a longer break
// simply is not in there, so `collectSessions` returns [] and the panel says
// "No history yet" — which is FALSE. The history exists, on the server, one
// indexed query away. He met this on 2026-08-25 reintroducing the deadlift:
// *«non avere valori è cecità»*.
//
// ⛔ The cure is NOT a bigger window. Widening the session fetch would pull
// months of every other exercise to answer a question about one, and
// performance is one of his two stated constraints on this work. The right
// move is to ask the right question: `GET /api/sets?exercise=<slug>` rides
// the sparse `gsi1` partition (`USER#<sub>#EX#<slug>`) that exists for
// exactly this — the route's own comment calls it the "90% read path (PR
// trend, e1RM curve, when did I last squat)". One query, newest-first.
//
// ⭐ It answers the OTHER constraint too — offline. The resource caches per
// slug in AsyncStorage, so the second time the athlete opens that exercise at
// the gym the age and the last sets are on screen before the network replies.
//
// ⛔ And it distinguishes "no sets" from "don't know yet". `resolved` flips
// only once an answer actually arrived; until then the panel must not print
// an empty state, because a cold, offline card would then state as fact the
// very thing this file was written to stop it stating
// ([[feedback_empty_vs_unresolved_collection]]).

// One indexed query. 25 rather than the 5 the panel prints: the extra rows
// cost nothing on this partition and carry the all-time best + enough days to
// group, so a second request is never needed to say more.
const FALLBACK_SET_LIMIT = 25;

// Single-flight, per slug. Two surfaces read the same exercise at the same
// time — the age pill on the card and the history panel in the sheet opened
// over it — and each mount runs its own `useAsyncResource`. Without this they
// would issue the same query twice, which is the sort of waste that gets
// noticed only as "why is it slow at the gym". Keyed by slug, cleared when it
// settles, so nothing is cached here beyond the request itself: staleness
// stays `useAsyncResource`'s job (and AsyncStorage's), not this map's.
const inFlight = new Map<string, Promise<LogSetT[]>>();

const singleFlight = (
  slug: string,
  run: () => Promise<LogSetT[]>,
): Promise<LogSetT[]> => {
  const running = inFlight.get(slug);
  if (running) return running;
  const p = run().finally(() => {
    if (inFlight.get(slug) === p) inFlight.delete(slug);
  });
  inFlight.set(slug, p);
  return p;
};

const cacheKeyFor = (exerciseId: string) => `logger/exerciseSets/${exerciseId}`;

const queryExerciseSets = (
  exerciseId: string,
  apiFetch: ProgramApiFetch,
): Promise<LogSetT[]> =>
  singleFlight(exerciseId, async () => {
    const res = await apiFetch<{ items?: unknown[] }>(
      `/api/sets?exercise=${encodeURIComponent(exerciseId)}&limit=${FALLBACK_SET_LIMIT}`,
    );
    const out: LogSetT[] = [];
    for (const raw of res.items ?? []) {
      // Parse-and-drop, like every other read path here: one row that
      // drifted from the schema must not cost the athlete the other 24.
      const parsed = LogSet.safeParse(raw);
      if (parsed.success) out.push(parsed.data);
    }
    return out;
  });

/**
 * The same door, opened from OUTSIDE React (LG-71).
 *
 * ⭐ Why an imperative twin and not a second hook: the add-exercise prefill
 * runs in an event handler, above the Provider and after a dispatch, so it
 * cannot mount anything. What it must NOT do is issue its own request — that
 * would be a second query, a second cache key and a second answer for the same
 * question. It shares this module's `singleFlight` map and AsyncStorage key
 * with the hook below, so the card's age pill and the prefill asking about the
 * same slug in the same breath cost ONE query between them.
 *
 * ⭐ Cache FIRST, then the network. The hook is an SWR surface and wants the
 * fresh answer; this caller has one shot before the athlete starts typing, and
 * an instant answer off disk beats a better one that arrives after they have
 * filled the cell by hand. Staleness costs nothing here: an exercise trained
 * recently enough for the cache to be wrong is one the rolling window already
 * answers, so this path is not consulted for it at all.
 *
 * ⛔ Resolves to `[]` on failure, like the window's own fetch. The caller must
 * read that as "nothing to offer", never as "this exercise has no history"
 * ([[feedback_auth_failure_is_not_a_verdict]]) — it prefills nothing either
 * way, and the age pill stays the thing that states an age.
 */
export async function loadExerciseSets(
  exerciseId: string,
  apiFetch: ProgramApiFetch,
  /** `maxAgeMs`: a cached answer older than this asks the network again, and
   *  stays the answer when the network fails (P0 57d74690). */
  opts: { maxAgeMs?: number } = {},
): Promise<LogSetT[]> {
  const cache = asyncStorageResourceCache<LogSetT[]>(cacheKeyFor(exerciseId));
  let stale: LogSetT[] | null = null;
  try {
    const cached = await cache.readEntry();
    // ⛔ `!== null`, not `.length > 0`: only a SERVED answer is ever written
    // here (the failure path below writes nothing), so a cached empty list is
    // the server having said "no history", which is an answer. Reading it as
    // "no cache" would re-ask the same question on every add of an exercise
    // that has genuinely never been done — and would make this door disagree
    // with the hook's own cache read about the same key.
    if (cached !== null) {
      const age = cached.cachedAtMs === null ? Infinity : Date.now() - cached.cachedAtMs;
      if (opts.maxAgeMs === undefined || age <= opts.maxAgeMs) return cached.value;
      stale = cached.value;
    }
  } catch {
    // A cache that cannot be read is simply not there.
  }
  try {
    const fresh = await queryExerciseSets(exerciseId, apiFetch);
    void cache.write(fresh);
    return fresh;
  } catch {
    return stale ?? [];
  }
}

export type ExerciseSetsFallback = {
  /** Sets for this slug, newest-first. Null while genuinely unknown. */
  sets: readonly LogSetT[] | null;
  /** True once an answer arrived. Only then does an empty list mean empty. */
  resolved: boolean;
  loading: boolean;
};

/**
 * @param exerciseId Slug to resolve.
 * @param needed Gate. False when the rolling window already answered, so the
 *   common case — an exercise trained this month — costs no request at all.
 *   Callers pass it for the ACTIVE card only, never for every entry at once.
 */
export function useExerciseSetsFallback(
  exerciseId: string,
  needed: boolean,
  apiFetch: ProgramApiFetch,
): ExerciseSetsFallback {
  const cacheKey = cacheKeyFor(exerciseId);

  const cache = useMemo(
    () => asyncStorageResourceCache<LogSetT[]>(cacheKey),
    [cacheKey],
  );

  const fetchSets = useMemo(
    () => (): Promise<LogSetT[]> => queryExerciseSets(exerciseId, apiFetch),
    [apiFetch, exerciseId],
  );

  const resource = useAsyncResource<LogSetT[]>({
    key: cacheKey,
    fetch: fetchSets,
    cache,
    immediate: needed,
  });

  return {
    sets: resource.value,
    // `error` deliberately does NOT count as resolved: a failed request is
    // silence, not the answer "none" ([[feedback_auth_failure_is_not_a_verdict]]).
    resolved: resource.status === 'loaded' || resource.status === 'refreshing',
    loading: resource.status === 'loading',
  };
}
